/**
 * Computes the value stamped into the `Sampled` column: the percent of the
 * population a logged row represents, so consumers can extrapolate the true
 * volume as `count * 100 / Sampled`. 100 means the row is fully captured.
 *
 * The flight-control rate (app/ECS sampling) and a per-datapoint
 * `sessionSampleRate` are independent samplers, so when both apply the effective
 * captured fraction is their product.
 *
 * Honoring `sessionSampleRate` here makes the datapoint's own logging setting
 * part of the source of truth for `Sampled`. Previously this main logging path
 * ignored it (while the OOBE lite path already stamped it), so self-sampled
 * events were stamped 100 and under-counted by up to ~1000x.
 */
export default function getSampledRate(flightControlRate: number | undefined, sessionSampleRate: number | undefined): number {
    const flightRate = flightControlRate && flightControlRate > 0 ? flightControlRate : undefined;
    // A non-positive sessionSampleRate means "no session sampling", matching the
    // logging gate (shouldLogDatapoint's `!sessionSampleRate`, which logs such
    // datapoints in full). Treating 0 as an active rate would stamp Sampled=0 and
    // make `count * 100 / Sampled` divide by zero.
    const sessionRate = sessionSampleRate != null && sessionSampleRate > 0 && sessionSampleRate < 100
        ? sessionSampleRate
        : undefined;
    
    if (flightRate !== undefined && sessionRate !== undefined) {
        return (flightRate * sessionRate) / 100;
    }
    
    return flightRate ?? sessionRate ?? 100;
}
